import '../../styles/_player.scss';

import JWPlayer from '@jwplayer/jwplayer-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDebounceCallback } from 'usehooks-ts';

import { useConnectivity } from '../../contexts/ConnectivityContext';
import { MenuOptions } from '../../enums/MenuOptions';
import { ScreenSizeQueries } from '../../enums/ScreenSizeQueries';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
    AnalyticsItemsResult,
    FetchType,
    loadAnalyticsItemsWithFallback,
    useAnalytics,
} from '../../hooks/useAnalytics';
import { ICaptionsList, useCaptions } from '../../hooks/useCaptions';
import { useCleRetryHandler } from '../../hooks/useCleRetryHandler';
import { useCleTimer } from '../../hooks/useCleTimer';
import useDownload from '../../hooks/useDownload';
import { useFetchTranscripts } from '../../hooks/useFetchTranscripts';
import { LiveCaptionsAnalyticsEvent, useLiveCaptions } from '../../hooks/useLiveCaptions';
import { useLiveEndOfProgramTimer } from '../../hooks/useLiveEndOfProgramTimer';
import { MediaLogEventTypes, useLogMediaEvents } from '../../hooks/useLogMediaEvents';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import useMobileSlideToggle from '../../hooks/useMobileSlideToggle';
import { useMux } from '../../hooks/useMux';
import { usePlaybackOptimization } from '../../hooks/usePlaybackOptimization';
import usePlaybackProgress from '../../hooks/usePlaybackProgress';
import usePlayerDiagnostics from '../../hooks/usePlayerDiagnostics';
import {
    IQualityLevels,
    IVisualQualityEvent,
    usePlayerSettings,
} from '../../hooks/usePlayerSettings';
import { PlaylistItem, usePlaylist } from '../../hooks/usePlaylist';
import { useSlides } from '../../hooks/useSlides';
import { StallNudgeDetails, useStallWatchdog } from '../../hooks/useStallWatchdog';
import { useStsTimer } from '../../hooks/useStsTimer';
import { useThrottleCallback } from '../../hooks/useThrottleCallback';
import { useToasts } from '../../hooks/useToasts';
import useTranscriptButton from '../../hooks/useTranscriptButton';
import { ItemDataLayer } from '../../utility/dataLayerUtils';
import { isTouchDevice } from '../../utility/device';
import logger from '../../utility/logger';
import platform from '../../utility/platform';
import {
    findCappedQualityIndex,
    isConstrainedNetwork,
    readNetworkConditions,
    shouldReleaseQualityCap,
} from '../../utility/playbackOptimization';
import IdleOverlay from '../IdleOverlay/IdleOverlay';
import Loader from '../Loader/Loader';
import { OffAir } from '../OffAir/OffAir';
import { menuData } from '../SettingsMenu/MenuData';
import SettingsMenu from '../SettingsMenu/SettingsMenu';
import { setLargePanelOpenCommand } from '../Store/menuSlice';
import {
    ExternalCommand,
    ExternalFullscreenToggle,
    setExternalCommand,
    setExternalFullscreenToggle,
    setExternalSeekCommand,
    setPlayerElementReady,
} from '../Store/playerSlice';
import ThumbnailContainer from '../ThumbnailContainer/ThumbnailContainer';
import { ToastContainer } from '../Toast/ToastContainer';
import Transcripts from '../Transcripts/Transcripts';

export interface IPlayerProps {
    autostart?: boolean,
    showThumbnail?: boolean,
    isMenuOpen?: boolean,
}

export interface OnTimeEvent {
    position: number;
    duration: number;
    currentTime: number;
    seekRange?: {
      start: number;
      end: number;
    };
    metadata?: {
      currentTime: number;
    };
    absolutePosition?: number; // Optional since it can be null
    type?: 'time';
    viewable?: 1;
}

export const shouldTrackLiveCaptionDelivery = (
    isCleTimerRunning: boolean,
    isBuffering: boolean,
) => isCleTimerRunning && !isBuffering;

export const Player = (
    {
        autostart = false,
        showThumbnail = false,
        isMenuOpen = false,
    }: IPlayerProps) => {

    const user = useAppSelector((state) => state.user);
    const launchDetails = useAppSelector((state) => state.player.launchDetails);
    const liveCaptionsAllowed = Boolean(launchDetails.isWebcast) && user.hasAiAccess;

    const dispatch = useAppDispatch();
    const playerRef = useRef<typeof JWPlayer>(null);
    const videoRef = useRef<HTMLVideoElement>();
    const isInitialPlay = useRef<number | null>(null);
    const currentSegmentItemSk = useAppSelector((state) => state.player.currentSegmentItemSk);
    const captionSessionGuid = useAppSelector((state) => state.player.sessionGuid);

    const { logMediaEvent } = useLogMediaEvents();

    const { connected } = useConnectivity();

    const hasLoggedInitialLoad = useRef(false);

    const { playlist, segment, fallbackToStandardVideo } = usePlaylist(liveCaptionsAllowed);
    const currentPlaylistItem = playlist[0];
    const currentMediaFile = currentPlaylistItem?.file ?? '';
    const currentPlaylistStartTime = currentPlaylistItem?.starttime ?? 0;
    const { emitVideoChangeEvent, initializeMux } = useMux();
    const {
        isEnabledForOnDemand,
        isOptimized: isPlaybackOptimized,
        options: playbackOptions,
        jwConfig: optimizedJwConfig,
    } = usePlaybackOptimization();
    const { transcripts } = useFetchTranscripts();
    const [currentFilePlaying, setCurrentFilePlaying] = useState('');
    const [thumbnailPosition, setThumbnailPosition] = useState('');
    const didImagesFinishCaching = useAppSelector((state) => state.player.didPosterImageFinishCaching);
    const isFetching = useAppSelector((state) => state.player.isFetchingLaunchDetails);
    const [isMetaDataReady, setIsMetaDataReady] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const [isLiveCaptionDeliveryBuffering, setIsLiveCaptionDeliveryBuffering] = useState(false);

    const clientConfig = useAppSelector((state) => state.clientConfig);
    const videoSourceType = useAppSelector((state) => state.player.videoSourceType);
    const streamCleared = useAppSelector((state) => state.player.streamCleared);

    const [showIdleOverlay, setShowIdleOverlay] = useState(false);
    const [lastInteraction, setLastInteraction] = useState('');
    const [playerElement, setPlayerElement] = useState<HTMLElement | null>(null);
    const [controlBarState, setControlBarState] = useState('default');
    const { getItems, pushToDataLayer } = useAnalytics();
    const liveCaptionsFallbackProgramItems = useMemo<ItemDataLayer[]>(() => [{
        item_id: launchDetails.itemPk ?? undefined,
        item_name: launchDetails.title ?? undefined,
        affiliation: 'PLI',
        index: 0,
        item_brand: 'PLI',
        item_list_id: 'watch',
        item_list_name: 'watch',
        currency: 'USD',
    }], [launchDetails.itemPk, launchDetails.title]);
    const liveCaptionsProgramItemsRef = useRef<AnalyticsItemsResult>({
        items: liveCaptionsFallbackProgramItems,
        source: 'fallback',
    });

    useEffect(() => {
        liveCaptionsProgramItemsRef.current = {
            items: liveCaptionsFallbackProgramItems,
            source: 'fallback',
        };
        if (!liveCaptionsAllowed) return;

        let active = true;
        void loadAnalyticsItemsWithFallback({
            load: () => getItems({ fetchType: FetchType.Program }),
            fallbackItems: liveCaptionsFallbackProgramItems,
        }).then((result) => {
            if (active) liveCaptionsProgramItemsRef.current = result;
        });

        return () => {
            active = false;
        };
    }, [getItems, liveCaptionsAllowed, liveCaptionsFallbackProgramItems]);

    const handleLiveCaptionsAnalytics = useCallback((event: LiveCaptionsAnalyticsEvent) => {
        const programItems = liveCaptionsProgramItemsRef.current;
        void pushToDataLayer({
            event: event.request_type === 'service'
                ? 'live_captions_service'
                : 'live_captions_learner',
            additionalParams: {
                ...event,
                program_data_source: programItems.source,
            },
            fetchType: FetchType.Program,
            items: programItems.items,
        });
    }, [pushToDataLayer]);
    const idleOverlayTimeoutRef = useRef<number>();
    const controlBarTimeoutRef = useRef<number>();
    const webcastFinishedRef = useRef(false);

    const isSmallScreen = useMediaQuery(ScreenSizeQueries.JWSMALL);

    const { isCleTimerRunning, startCleTimer, pauseCleTimer } = useCleTimer({
        playerRef,
    });
    const { retryFailedTimestamps } = useCleRetryHandler();

    const { isStsTimerRunning, startStsTimer, setIsPlaying } = useStsTimer({
        playerRef: playerRef
    });

    const { setPlaybackProgress, postLatestPlaybackProgress } = usePlaybackProgress();
    useLiveEndOfProgramTimer({
        playerRef: playerRef
    });

    const { showAlertToast } = useToasts();
    const {
        handleErrorDiagnostics,
        handlePlaybackStoppedDiagnostics,
        handlePlayDiagnostics,
        handleReadyDiagnostics,
        handleSeekDiagnostics,
        handleSeekedDiagnostics,
        handleTimeDiagnostics,
        registerSeekIntent,
        requestCommand,
        syncBrowserMediaDiagnostics,
    } = usePlayerDiagnostics({
        currentMediaFile,
        currentPlaylistStartTime,
        playerRef: playerRef as React.MutableRefObject<{ getPosition?: () => number; getDuration?: () => number } | null>,
        videoSourceType,
        videoElementRef: videoRef as React.MutableRefObject<HTMLVideoElement | undefined>,
    });

    const {
        handleCaptionsList,
        handleCaptionsChanged,
        captions,
        captionsButtonIcon,
    } = useCaptions({
        playerRef: playerRef,
        enabled: !launchDetails?.isWebcast,
    });
    const {
        captionsAvailable: liveCaptionsAvailable,
        handleLiveCaptionsMetadata,
        handleLiveCaptionsFailure,
    } = useLiveCaptions({
        playerRef,
        enabled: liveCaptionsAllowed,
        captionsButtonIcon,
        cleTrackingActive: shouldTrackLiveCaptionDelivery(
            isCleTimerRunning,
            isLiveCaptionDeliveryBuffering,
        ),
        programItemSk: launchDetails.itemSk,
        segmentItemSk: currentSegmentItemSk,
        sessionGuid: captionSessionGuid,
        environment: clientConfig.datadog?.env,
        onAnalyticsEvent: handleLiveCaptionsAnalytics,
    });
    const { 
        viewSlides,
        displayToggleSlidesButton, 
        slidesEnabled,
        setSlidesEnabled,
    } = useMobileSlideToggle({
        playerRef: playerRef,
        toggleTranscriptButton: () => onToggleTranscript(false),
    });
    const { showTranscript, onToggleTranscript, displayToggleTranscriptButton, handleTranscriptsButtonRef } = useTranscriptButton(playerRef);
    const { displayDownloadButton } = useDownload();

    const {
        showSettingsMenu,
        displayToggleSettingsButton,
        handleSettingsButtonRef,
        handleCloseSettings,
        settingsButtonRef,
        handleQualityLevels,
        setSettingsMenu,
        settingsMenu,
        qualitySettings,
        setQualitySettings,
        captionsSettings,
        transcriptSettings,
        setTranscriptSettings,
        handleFontSize,
        toggleQualitySettingsDisabled,
    } = usePlayerSettings({
        playerRef: playerRef,
        hasLiveCaptions: liveCaptionsAvailable,
    });

    const { ivsPlayer, currentSlide, handleProviderPlayer } = useSlides({
        onLiveCaptionsMetadata: handleLiveCaptionsMetadata,
        onLiveCaptionsFailure: handleLiveCaptionsFailure,
        liveCaptionsAllowed,
    });
    const externalCommand = useAppSelector((state) => state.player.externalCommand);

    const logBufferingMediaEvent = useDebounceCallback(useCallback((timestamp: number) => {
        logMediaEvent(MediaLogEventTypes.BUFFERING, 'Buffering...', timestamp);
    }, [logMediaEvent]), 5_000);

    const logConnectivityLostEvent = useThrottleCallback(useCallback((timestamp: number) => {
        logMediaEvent(MediaLogEventTypes.CONNECTION_LOST, 'Internet connection lost', timestamp, true);
    }, [logMediaEvent]), 5_000);

    const logConnectivityRestoredEvent = useThrottleCallback(useCallback((timestamp: number) => {
        logConnectivityLostEvent.cancel();
        logMediaEvent(MediaLogEventTypes.CONNECTION_RESTORED, 'Internet connection restored', timestamp);
    }, [logConnectivityLostEvent, logMediaEvent]), 5_000);

    const handleStallNudge = useCallback((details: StallNudgeDetails) => {
        const isFrameFreeze = details.kind === 'frames';
        const budget = isFrameFreeze
            ? playbackOptions.frameFreezeMaxNudges
            : playbackOptions.stallWatchdogMaxNudges;

        logger.log('Stall watchdog recovered a silent freeze', {
            individualSk: user.individualSK,
            itemSk: launchDetails.itemSk,
            ...details,
        });

        // The two shapes are logged distinctly on purpose: a frozen picture with healthy audio is a
        // decode failure, a frozen playhead is a delivery failure, and merging them in the event log
        // is what made the freezing complaints unattributable in the first place.
        logMediaEvent(
            MediaLogEventTypes.BUFFERING,
            `Stall watchdog ${isFrameFreeze ? 'frame-freeze' : 'playhead-stall'} micro-seek `
            + `${details.attempt}/${budget} at ${details.position.toFixed(2)}s `
            + `after ${details.stalledForMs}ms (readyState ${details.readyState}, `
            + `${details.bufferedAhead.toFixed(2)}s buffered ahead, `
            + `frames ${details.totalVideoFrames ?? 'unavailable'})`
        );
    }, [
        launchDetails.itemSk,
        logMediaEvent,
        playbackOptions.frameFreezeMaxNudges,
        playbackOptions.stallWatchdogMaxNudges,
        user.individualSK,
    ]);

    const { resetStallWatchdog } = useStallWatchdog({
        videoElementRef: videoRef,
        enabled: isPlaybackOptimized,
        isBuffering,
        thresholdMs: playbackOptions.stallWatchdogThresholdMs,
        maxNudges: playbackOptions.stallWatchdogMaxNudges,
        nudgeSeconds: playbackOptions.stallWatchdogNudgeSeconds,
        frameFreezeThresholdMs: playbackOptions.frameFreezeThresholdMs,
        frameFreezeMaxNudges: playbackOptions.frameFreezeMaxNudges,
        onNudge: handleStallNudge,
    });

    useEffect(() => {
        resetStallWatchdog();
    }, [currentMediaFile, currentSegmentItemSk, resetStallWatchdog]);

    useEffect(() => {
        logMediaEvent(MediaLogEventTypes.PROGRAM_LAUNCH, platform.ua);
    }, [logMediaEvent]);

    const previousConnectedState = useRef(connected);
    useEffect(() => {
        if (previousConnectedState.current !== connected) {
            if (connected) {
                logConnectivityRestoredEvent(Date.now());
            } else {
                logConnectivityLostEvent(Date.now());
            }
        }
        previousConnectedState.current = connected;
    }, [connected, logConnectivityLostEvent, logConnectivityRestoredEvent]);

    useEffect(() => {
        if (playerRef.current) {
            switch (externalCommand) {
                case ExternalCommand.play:
                    requestCommand('play', 'Issued via external player command.');
                    playerRef.current.play();
                    dispatch(setExternalCommand(ExternalCommand.noop));
                    break;
                case ExternalCommand.pause:
                    requestCommand('pause', 'Issued via external player command.');
                    playerRef.current.pause();
                    dispatch(setExternalCommand(ExternalCommand.noop));
                    break;
                case ExternalCommand.noop:
                    break;
            }
        }
    }, [externalCommand, dispatch, requestCommand]);

    useEffect(() => {
        if (launchDetails.isWebcast) {
            if (currentSlide?.slideNumber && currentSlide?.slideNumber > 0) {
                setSlidesEnabled(true);
            } else {
                setSlidesEnabled(false);
            }
        }
    }, [slidesEnabled, currentSlide?.slideNumber, launchDetails.isWebcast, setSlidesEnabled]);

    const externalFullscreenToggle = useAppSelector((state) => state.player.externalFullscreenToggle);

    useEffect(() => {
        if (playerRef.current) {

            switch (externalFullscreenToggle) {
                case ExternalFullscreenToggle.on:
                    playerRef.current.setFullscreen(true);
                    dispatch(setExternalFullscreenToggle(ExternalFullscreenToggle.noop));
                    break;
                case ExternalFullscreenToggle.off:
                    playerRef.current.setFullscreen(false);
                    dispatch(setExternalFullscreenToggle(ExternalFullscreenToggle.noop));
                    break;
                case ExternalFullscreenToggle.noop:
                    break;
            }
        }
    }, [externalFullscreenToggle, dispatch]);

    const externalSeekCommand = useAppSelector((state) => state.player.externalSeekCommand);

    useEffect(() => {
        if (playerRef.current && externalSeekCommand !== undefined) {
            requestCommand('seek', `Requested seek to ${Math.round(externalSeekCommand)}s via external player command.`);
            registerSeekIntent(
                externalSeekCommand,
                'external-command',
                playerRef.current.getPosition?.() ?? 0
            );
            playerRef.current.seek(externalSeekCommand);
            dispatch(setExternalSeekCommand(undefined));
        }
    }, [dispatch, externalSeekCommand, registerSeekIntent, requestCommand]);

    useEffect(() => {
        if (isMenuOpen) {
            clearTimeout(idleOverlayTimeoutRef.current);
            idleOverlayTimeoutRef.current = undefined;
        } 
    }, [isMenuOpen]);

    const displayTitle = useCallback(() => {
        const node = document.querySelector('.jw-spacer');
        const programTitle = document.querySelector('.jwplayer-custom__program-title');
        if (node && !programTitle) {
            const title = document.createElement('div');
            title.textContent = (segment?.title ? segment?.title : launchDetails?.title) || '';
            title.classList.add('jwplayer-custom__program-title', 'paragraph-2');
            node.before(title);
        } else if (programTitle) {
            programTitle.textContent = (segment?.title ? segment?.title : launchDetails?.title) || '';
        }
    }, [launchDetails?.title, segment?.title]);

    const holdProgressUpdates = useRef(true);
    const hasLoadedInitialPlaylist = useRef(false);

    const loadPlaylist = useDebounceCallback(useCallback((playlist: PlaylistItem[]) => {
        if (!playerRef.current || !playlist.length) return;
        playerRef.current.load(playlist);
        if (hasLoadedInitialPlaylist.current) {
            playerRef.current.play();
        }
        hasLoadedInitialPlaylist.current = true;
    }, []), 300);

    useEffect(() => {
        if (!segment?.itemSk) return;

        // reset progress updates until next segment is finally loaded
        holdProgressUpdates.current = true;
    }, [segment?.itemSk]);

    useEffect(() => {
        setControlBarState('hidden');
        displayTitle();
        setCurrentFilePlaying(playlist[0]?.file ?? '');

        if (!playerRef.current || !playlist.length)
            return;

        const isInitialLoad = !hasLoadedInitialPlaylist.current;

        loadPlaylist(playlist);

        // The debounce exists to coalesce rapid playlist churn, but on the very first load there is
        // nothing to coalesce and it costs a flat 300ms of Time To First Frame.
        if (isInitialLoad && isEnabledForOnDemand) {
            loadPlaylist.flush();
        }

        if (currentFilePlaying && currentFilePlaying !== playlist[0].file) {
            emitVideoChangeEvent(playerRef, segment!);
        }
    }, [currentFilePlaying, displayTitle, emitVideoChangeEvent, isEnabledForOnDemand, loadPlaylist, playlist, segment]);

    useEffect(() => {
        if (showSettingsMenu) {
            setControlBarState('forced');
        } else {
            setControlBarState('default');
        }
    }, [showSettingsMenu]);

    useEffect(() => {
        displayToggleSettingsButton();
    }, [captions?.track, captions?.tracks, qualitySettings, displayToggleSettingsButton]);

    const swapVolumeAndLive = () => {
        const volumeButton = document.querySelectorAll('.jw-icon-volume')[0];
        const textLive = document.querySelectorAll('.jw-text-live')[0];

        if (volumeButton && textLive) {
            const parentElement = volumeButton.parentElement;

            if (parentElement) parentElement.insertBefore(textLive, volumeButton);
        }
    };

    const setupViews = () => {
        displayTitle();

        if (isSmallScreen && playerRef.current) {
            displayToggleSlidesButton(viewSlides);
        }

        setSettingsMenu(menuData);
        displayToggleSettingsButton();
        displayToggleTranscriptButton(showTranscript);
        displayDownloadButton(playerRef);

        if (launchDetails?.isWebcast) {
            swapVolumeAndLive();
        }
    };

    const attachEvents = (playerElement: HTMLElement) => {
        // Live webcast only (no mobile)
        if (!launchDetails.isWebcast || isSmallScreen) return;

        playerElement.addEventListener('keydown', e => {
            // prevent pause using keyboard
            if (![' ', 'Space', 'Enter'].includes(e.key)) return;

            e.stopImmediatePropagation();
        }, true);

        playerElement.querySelectorAll('video, .jw-media').forEach(element => {
            if (!element.matches('video') && isTouchDevice()) return;

            element.addEventListener('click', e => {
                e.preventDefault();
                e.stopImmediatePropagation();
            }, true);
        });
    };

    const onReady = () => {
        setupViews();
        handleReadyDiagnostics();

        const playerElement = document.getElementById('player');

        if (playerElement) {
            playerElement.addEventListener('mousedown', () => {
                setLastInteraction('mouse');
            });

            playerElement.addEventListener('keyup', () => {
                setLastInteraction('keyboard');
            });

            setPlayerElement(playerElement);

            dispatch(setPlayerElementReady());

            attachEvents(playerElement);
        }
        
    };

    const onPlay = (event: any) => {
        webcastFinishedRef.current = false;
        setIsLiveCaptionDeliveryBuffering(false);

        if (holdProgressUpdates.current)
            holdProgressUpdates.current = false;
        handlePlayDiagnostics();
        
        logBufferingMediaEvent.cancel();
        clearTimeout(idleOverlayTimeoutRef.current);
        idleOverlayTimeoutRef.current = undefined;

        if (!isCleTimerRunning && (launchDetails?.trackCle && !launchDetails?.isPortable && !user.isGroupcast)) {
            startCleTimer();
        }

        if (!isStsTimerRunning && !user.isGroupcast) {
            startStsTimer();
        }

        setIsPlaying(true);

        if (bufferTimeoutRef.current) {
            clearTimeout(bufferTimeoutRef.current);
            bufferTimeoutRef.current = undefined;
        }
        setIsBuffering(false);

        // 'external' - (JW API usage)
        if (event.playReason === 'external') {
            setControlBarState('forced');
            controlBarTimeoutRef.current = setTimeout(() => {
                setControlBarState('default');
            }, 5000) as unknown as number;
        } else {
            if (controlBarState === 'hidden')
                setControlBarState('default');
        }

        if (isInitialPlay.current == null) {
            pushToDataLayer({ event: 'video_start', additionalParams: { started_from: 'launch' } });
        }

        if (isInitialPlay.current !== currentSegmentItemSk) {
            isInitialPlay.current = currentSegmentItemSk;
        }
    };

    // Tracks the quality index JW last reported, via every levelsChanged event — including ones
    // fired by a viewer's own manual selection in the settings menu. The release timer below reads
    // this at fire time to tell "still on the cap we set" apart from "the viewer picked something
    // else since".
    const latestQualityIndexRef = useRef<number | null>(null);

    const onLevelsChanged = (event: IQualityLevels) => {
        latestQualityIndexRef.current = event?.currentQuality ?? null;
        setQualitySettings(event);
        logger.log('JW Player levelsChanged event:', event, false);
    };

    const hasAppliedInitialQualityCap = useRef(false);
    const capReleaseTimeoutRef = useRef<number>();

    useEffect(() => () => clearTimeout(capReleaseTimeoutRef.current), []);

    /**
     * Holds the first renditions down on a connection the browser reports as constrained, then
     * hands adaptive selection back — but only if the viewer is still on the rendition this code
     * chose. Selecting an explicit quality in JW turns ABR off, and that is exactly what a manual
     * pick during the cap window does too: without this check, a viewer who deliberately chose a
     * quality in those 15 seconds would be silently reset to Auto, undoing their choice and, on a
     * genuinely constrained connection, risking the very stalling this feature exists to prevent.
     */
    const applyConstrainedStartupCap = useCallback((event: IQualityLevels) => {
        if (!isPlaybackOptimized || hasAppliedInitialQualityCap.current) return;
        if (playbackOptions.constrainedInitialHeightCap <= 0) return;
        if (!isConstrainedNetwork(readNetworkConditions())) return;

        const cappedIndex = findCappedQualityIndex(
            event?.levels,
            playbackOptions.constrainedInitialHeightCap
        );

        if (cappedIndex === null || !playerRef.current) return;

        hasAppliedInitialQualityCap.current = true;
        playerRef.current.setCurrentQuality(cappedIndex);
        latestQualityIndexRef.current = cappedIndex;

        logger.log('Constrained network detected, capping startup quality', {
            cappedIndex,
            heightCap: playbackOptions.constrainedInitialHeightCap,
            releaseMs: playbackOptions.constrainedCapReleaseMs,
        });

        capReleaseTimeoutRef.current = setTimeout(() => {
            if (!shouldReleaseQualityCap(latestQualityIndexRef.current, cappedIndex)) {
                logger.log('Skipping quality cap release: viewer selected a different quality during the cap window', {
                    cappedIndex,
                    currentQuality: latestQualityIndexRef.current,
                });
                return;
            }

            playerRef.current?.setCurrentQuality(0);
            logger.log('Released startup quality cap, adaptive selection restored');
        }, playbackOptions.constrainedCapReleaseMs) as unknown as number;
    }, [
        isPlaybackOptimized,
        playbackOptions.constrainedCapReleaseMs,
        playbackOptions.constrainedInitialHeightCap,
    ]);

    const onLevels = useCallback((event: IQualityLevels) => {
        handleQualityLevels(event);
        applyConstrainedStartupCap(event);
    }, [applyConstrainedStartupCap, handleQualityLevels]);

    const onVisualQuality = (event: IVisualQualityEvent) => {
        logger.log('JW Player visualQuality event:', event, false);
    };

    const onControls = (controls: boolean) => {
        if (controls) {
            displayTitle();
        }
    };

    const onTime = (event: OnTimeEvent) => {
        // https://github.com/jwplayer/jwplayer/issues/3339
        if (playerRef.current.getConfig().scrubbing) return;
        handleTimeDiagnostics(event);

        if (!hasLoggedInitialLoad.current) {
            logMediaEvent(MediaLogEventTypes.START_VIDEO, 'on play (first time)');
            hasLoggedInitialLoad.current = true;
        }

        if (!holdProgressUpdates.current) {
            setPlaybackProgress({ position: event.position, shouldTrackVideoProgress: true, duration: event.duration });
        }
    };

    const onSeek = (event: any) => {
        // https://github.com/jwplayer/jwplayer/issues/3339
        if (playerRef.current.getConfig().scrubbing) return;
        handleSeekDiagnostics(event);

        if (!holdProgressUpdates.current) {
            setPlaybackProgress({ position: event.offset, postImmediately: true });
        }
    };

    const onSeeked = () => {
        handleSeekedDiagnostics();
    };

    const onError = (event: any) => {
        handleErrorDiagnostics(event);
        // Attempt to fallback to standard video if HLS fails
        const didFallback = fallbackToStandardVideo();

        if (!didFallback) {
            showAlertToast(event);
            handlePlaybackStopped(event);
        }

        logMediaEvent(MediaLogEventTypes.ERROR, `VideoPlayer error: [${event.code} - ${event.type}] ${event.message}${
            didFallback ? ' - Falling back to standard URL' : ''
        }`);
    };

    const bufferTimeoutRef = useRef<number>();

    const handlePlaybackStopped = (event: any) => {
        if (launchDetails.isWebcast && event.type === 'complete') {
            webcastFinishedRef.current = true;
        }

        if (launchDetails.isWebcast && event.type === 'pause') {
            playerRef.current.play();
            return;
        }

        if (launchDetails.isWebcast && event.type === 'idle') {
            const hasWebcastExpired = Boolean(
                launchDetails.timeExpiresUtc
                && Date.now() >= new Date(launchDetails.timeExpiresUtc).getTime()
            );

            const isOffAir = launchDetails.isOffAir || webcastFinishedRef.current || hasWebcastExpired;

            if (!isOffAir) {
                setTimeout(() => {
                    requestCommand('play', 'Retrying webcast playback from onIdle.');
                    playerRef.current.play();
                }, 100);
            }
        }

        handlePlaybackStoppedDiagnostics(event);

        if (!idleOverlayTimeoutRef.current && lastInteraction === 'mouse' && event.pauseReason === 'interaction' && !isMenuOpen) {
            idleOverlayTimeoutRef.current = setTimeout(() => {
                setShowIdleOverlay(true);
            }, 15 * 1000) as unknown as number;
        }

        if (isCleTimerRunning && event.type !== 'buffer') {
            logger.log('Pausing CLE Timer due to JW Player event', {
                individualSk: user.individualSK,
                itemSk: launchDetails.itemSk,
                event: event,
            });
            pauseCleTimer();
        }

        if (isStsTimerRunning && event.type !== 'buffer') {
            setIsPlaying(false);
        }

        if (event.type === 'buffer') {
            setIsLiveCaptionDeliveryBuffering(true);
            logBufferingMediaEvent(Date.now());
            if (bufferTimeoutRef.current) {
                clearTimeout(bufferTimeoutRef.current);
                bufferTimeoutRef.current = undefined;
            }
            bufferTimeoutRef.current = setTimeout(
                () => setIsBuffering(true), 1000) as unknown as number;
        }

        if (event.type === 'complete' && !launchDetails?.isWebcast) {
            logger.log('JW Player Media Complete event', {
                individualSk: user.individualSK,
                itemSk: launchDetails.itemSk,
                event: event,
            });
            pushToDataLayer({ event: 'video_complete' });
            dispatch(setLargePanelOpenCommand(MenuOptions.EndOfVideo));
            retryFailedTimestamps();
        }

        if (event.type === 'complete' || event.type === 'pause') {
            logBufferingMediaEvent.cancel();
            postLatestPlaybackProgress(); 
        }

    };

    useEffect(() => {
        if (!streamCleared) return;

        if (isCleTimerRunning) pauseCleTimer();
        setIsPlaying(false);

        clearTimeout(bufferTimeoutRef.current);
        bufferTimeoutRef.current = undefined;
        setIsBuffering(false);
        setIsLiveCaptionDeliveryBuffering(false);

        playerRef.current = null;
        videoRef.current = undefined;
        setPlayerElement(null);
    }, [isCleTimerRunning, pauseCleTimer, setIsPlaying, streamCleared]);

    const handleIdleOverlayInteraction = useCallback(() => {
        setShowIdleOverlay(false);
    }, []);

    const handleIdleOverlayKeyUp = useCallback((e: KeyboardEvent) => {
        handleIdleOverlayInteraction();
        if (playerRef.current && (e.target === playerElement))
            playerRef.current.play();
    }, [handleIdleOverlayInteraction, playerElement]);

    const handleMetaEvent = useCallback(() => {
        videoRef.current = document.querySelector('video.jw-video') as HTMLVideoElement;
        syncBrowserMediaDiagnostics(videoRef.current);
        handleSettingsButtonRef();
        handleTranscriptsButtonRef();

        if (!launchDetails?.isWebcast) {
            const captions: ICaptionsList = {
                tracks: playerRef.current.getCaptionsList(),
                track: playerRef.current.getCurrentCaptions()
            };

            // do not use this for onCaptionsList, because that call fires 
            // multiple times and too early for the current track to be known
            handleCaptionsList(captions);
        }
            
        setIsMetaDataReady(true);
         
    }, [handleTranscriptsButtonRef, handleSettingsButtonRef, launchDetails?.isWebcast, handleCaptionsList, syncBrowserMediaDiagnostics]);


    const controlBarClass = useCallback(() => {
        switch (controlBarState) {
            case 'hidden':
                return 'player-wrapper__hide-controlbar';
            case 'forced':
                return 'player-wrapper__force-controlbar';
            default:
                return '';
        }
    }, [controlBarState]);

    const swapOutHolder = useRef({ wasAuto: false, userQuality: null as any });
    const handleThumbnailSwap = useCallback((swapped: boolean) => {
        // LIVE only
        if (!ivsPlayer.current) return;

        if (swapped) {
            const wasAuto = swapOutHolder.current.wasAuto = qualitySettings?.levels[qualitySettings.currentQuality].label === 'auto';
            swapOutHolder.current.userQuality = ivsPlayer.current.getQuality();

            if (!wasAuto) {
                // will cause a quick rebuffer due to auto mode API not exposing the adaptive parameter as setQuality
                ivsPlayer.current.setAutoQualityMode(true);
            }
            ivsPlayer.current.setAutoMaxVideoSize(650, 370);
            logger.log('Swapping thumbnail, changing quality', swapOutHolder.current.userQuality);
            toggleQualitySettingsDisabled(true);
        } else {
            ivsPlayer.current.setAutoMaxVideoSize(9999, 9999);
            if (!swapOutHolder.current.wasAuto) {
                // this will disable auto mode while performing a smooth transition due to adaptive param
                ivsPlayer.current.setQuality(swapOutHolder.current.userQuality, true);
            }
            logger.log('Swapping out thumbnail, turning quality back', swapOutHolder.current.userQuality);
            // reset to default value
            swapOutHolder.current = { wasAuto: false, userQuality: null };
            toggleQualitySettingsDisabled(false);
        }
    }, [ivsPlayer, qualitySettings?.currentQuality, qualitySettings?.levels, toggleQualitySettingsDisabled]);

    const thumbnailPositionClass =
        (showThumbnail || (currentSlide && (currentSlide?.slideNumber ?? 0) > 0))
        && thumbnailPosition
            ? `thumbnail__${thumbnailPosition}`
            : '';

    const transcriptActiveClass = showTranscript ? 'transcript-active' : '';

    // The poster is decoration layered behind the video; waiting for it to cache before mounting
    // the player pushes Time To First Frame out by a whole image request.
    const canMountBeforePoster = isPlaybackOptimized && playbackOptions.mountBeforePosterCached;

    return (
        <div className={`player-wrapper ${controlBarClass()} ${launchDetails?.isWebcast ? 'webcast-overrides' : ''} ${thumbnailPositionClass} ${transcriptActiveClass}`}>
            {
                (playlist.length > 0 && (didImagesFinishCaching || canMountBeforePoster) && !launchDetails.isOffAir) &&
                <JWPlayer
                    id='player'
                    config={{
                        key: clientConfig!.playerKeyJW,
                        captions: {
                            color: captionsSettings.darkBackground === 'Light' ? '000000' : 'FFFFFF',
                            fontSize: captionsSettings.fontSize ? handleFontSize(captionsSettings.fontSize) : 5.25,
                            backgroundColor: captionsSettings.darkBackground === 'Light' ? 'FFFFFF' : '000000',
                            backgroundOpacity: 80
                        },
                        ...(optimizedJwConfig ?? {}),
                    }}
                    playlist={playlist}
                    width='100%'
                    height='100%'
                    autostart={autostart}
                    onReady={onReady}
                    onPlay={onPlay}
                    onPause={handlePlaybackStopped}
                    onBuffer={handlePlaybackStopped}
                    onIdle={handlePlaybackStopped}
                    onComplete={handlePlaybackStopped}
                    onError={onError}
                    onMeta={handleMetaEvent}
                    //onCaptionsList={handleCaptionsList}
                    onCaptionsChanged={handleCaptionsChanged}
                    onTime={onTime}
                    onProviderPlayer={handleProviderPlayer}
                    onSeek={onSeek}
                    onSeeked={onSeeked}
                    onLevels={onLevels}
                    onLevelsChanged={onLevelsChanged}
                    onVisualQuality={onVisualQuality}
                    pipIcon='disabled'
                    didMountCallback={(obj: typeof JWPlayer) => {
                        playerRef.current = obj.player;
                        initializeMux(playerRef, segment);
                    }}
                    onControls={onControls}
                />
            }
            {isFetching && <Loader />}
            {
                !streamCleared && isBuffering &&
                <Loader solidBackground={false} />
            }
            {
                !streamCleared &&
                (playlist.length === 0 || (!didImagesFinishCaching && !canMountBeforePoster) || isBuffering) &&
                <Loader solidBackground={false} />
            }
            {
                playerElement && (
                    createPortal((
                        <ToastContainer />
                    ),
                    playerElement)
                )
            }
            {
                isMetaDataReady && (showThumbnail || (currentSlide && (currentSlide?.slideNumber ?? 0) > 0)) && playerElement !== null &&
                createPortal(
                    <ThumbnailContainer
                        currentSlide={currentSlide}
                        thumbnailPosition={(position: string) => setThumbnailPosition(position)}
                        videoRef={videoRef}
                        showSlides={viewSlides}
                        onSwap={handleThumbnailSwap}
                    />,
                    playerElement
                )
            }
            {
                showIdleOverlay && playerElement !== null &&
                createPortal(
                    <IdleOverlay
                        onKeyUp={handleIdleOverlayKeyUp}
                        onMouseMove={handleIdleOverlayInteraction} />,
                    playerElement
                )
            }
            {
                showTranscript &&
                playerElement !== null &&
                createPortal(
                    <Transcripts
                        transcriptSettings={transcriptSettings} 
                    />,
                    playerElement
                )
            }
            {
                showSettingsMenu &&
                playerElement !== null &&
                createPortal(
                    <SettingsMenu
                        handleClose={handleCloseSettings}
                        isOpen={showSettingsMenu}
                        playerRef={playerRef}
                        settingsButtonRef={settingsButtonRef}
                        hasCaptions={liveCaptionsAvailable || Boolean(captions?.tracks && captions.tracks.length > 1)}
                        menuData={settingsMenu ?? menuData}
                        qualitySettings={qualitySettings}
                        hasTranscripts={transcripts.length > 0}
                        handleTranscriptSettings={setTranscriptSettings}
                        transcriptSettings={transcriptSettings} 
                    />,
                    playerElement
                )
            }
            {
                (launchDetails.isOffAir || streamCleared) &&
                <OffAir isStreamCleared={streamCleared} />
            }
        </div>
    );
};
